import { Alert, Feature, Link, ChecklistInfo } from '#/types/message';
import { useBranding } from '#/branding/BrandingContext';
import AlertIcon from '#/assets/warning_metadata.svg?react';
import FeatureIcon from '#/assets/feature.svg?react';
import LinkIcon from '#/assets/link.svg?react';
import './MetadataBubble.css';
import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface MetadataBubbleProps {
  alerts: Alert[];
  checklists?: ChecklistInfo[] | null;
  features?: Feature[];
  links?: Link[] | null;
  onAlertClick: (alerts: Alert[]) => void;
  onChecklistClick?: (checklistId: string) => void;
  onFeaturesClick?: () => void;
  onLinkClick: (url: string) => void;
  margin: boolean;
}

export function MetadataBubble({
  alerts,
  checklists,
  features,
  links,
  onAlertClick,
  onChecklistClick,
  onFeaturesClick,
  onLinkClick,
  margin
}: MetadataBubbleProps) {
  const [height, setHeight] = useState<number>(0);
  const contentRef = useRef<HTMLDivElement>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const branding = useBranding();
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? i18n.language;
  const baseLanguage = language.split('-')[0];
  const brandT = (key: string) =>
    branding.translations[language]?.[key] ??
    branding.translations[baseLanguage]?.[key] ??
    branding.translations.en?.[key] ??
    t(key);

  const hasContent = (alerts && alerts.length > 0) ||
    (checklists && checklists.length > 0) ||
    (features && features.length > 0) ||
    (links && links.length > 0)

  useEffect(() => {
    if (!contentRef.current) return;

    resizeObserverRef.current = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const newHeight = entry.contentRect.height;
        setHeight(newHeight);
      }
    });

    resizeObserverRef.current.observe(contentRef.current);

    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    if (hasContent && contentRef.current) {
      const rect = contentRef.current.getBoundingClientRect();
      setHeight(rect.height);
    } else {
      setHeight(0);
    }
  }, [hasContent, alerts, checklists, features, links]);

  return (
    <div
      className={`metadataBubble ${hasContent ? 'active' : ''} ${margin ? 'margin' : ''}`}
      style={{ height: hasContent ? `${height}px` : '0px' }}
    >
      <div ref={contentRef} className="metadataBubbleContent">
        {alerts && alerts.length > 0 && (
          <button
            key={1}
            className="alertTrigger"
            onClick={() => onAlertClick(alerts)}
          >
            <AlertIcon aria-hidden="true" />
            <span className="triggerLabel"> {t('safetyMessages')}</span>
          </button>
        )}

        {checklists && checklists.map((checklist, index) => (
          <button
            key={index}
            className="checklistTrigger"
            onClick={() => onChecklistClick?.(checklist.checklist_id)}
          >
            <span className="triggerLabel"> {brandT('checklist') + ': ' + checklist.title}</span>
          </button>
        ))}

        {features && features.length > 0 && (
          <button className="featureTrigger pulsing" onClick={onFeaturesClick}>
            <FeatureIcon aria-label="features" />
            <span className="triggerLabel">{t('selectOption') + ': ' + features[0].name}</span>
          </button>
        )}

        {links && links.map((link, index) => (
          <button
            key={index}
            className="linkTrigger"
            onClick={() => onLinkClick(link.url)}
          >
            <LinkIcon aria-label="link" />
            <span className="triggerLabel">{link.title}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
